import { useCallback, useEffect, useRef } from 'react';

/**
 * How long an element has to stay on screen before it counts as having been seen.
 *
 * Not padding. A page that jumps — the sale's category pills scroll with
 * `behavior: 'smooth'`, a hash link lands mid-page — drags the viewport across everything
 * in between, and each of those intersects for a frame or two. Without a dwell the page
 * would report an impression for every product the animation flew past: on a long list,
 * hundreds nobody ever looked at, which is worse than the missing events this exists to
 * fix. Short enough that a reader who stops is counted before they have finished reading
 * the title.
 */
const DWELL_MS = 300;

/**
 * Calls `onSeen` once the element has actually been on screen, and once only.
 *
 * @returns a ref to put on the element to watch.
 *
 * A callback ref rather than a `useRef` object so it can go on whatever element a card
 * already has, and so re-attaching it to a different node re-arms it. It stops watching as
 * soon as it has fired; a tile scrolled past twice is therefore one call, and so is one
 * unmounted and mounted again on the way back — but that second part is the *reporter's*
 * to remember, since the component does not survive the journey.
 *
 * No IntersectionObserver, no impressions. The alternative is reporting everything a page
 * drew as seen, which on a list that renders more than it shows is a made-up number.
 */
export default function useImpression(onSeen: () => void): (node: Element | null) => void {
  const report = useRef(onSeen);
  const stop = useRef<() => void>(() => {});

  // Kept current so a card that re-renders with a new position reports the new one,
  // without the ref below changing identity and re-arming the observer.
  useEffect(() => {
    report.current = onSeen;
  });

  useEffect(() => () => stop.current(), []);

  return useCallback((node: Element | null) => {
    stop.current();

    if (!node || typeof IntersectionObserver === 'undefined') {
      return;
    }

    let dwell: ReturnType<typeof setTimeout> | null = null;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        dwell = setTimeout(() => {
          observer.disconnect();
          report.current();
        }, DWELL_MS);
      } else if (dwell) {
        clearTimeout(dwell);
        dwell = null;
      }
    });

    observer.observe(node);

    stop.current = () => {
      if (dwell) {
        clearTimeout(dwell);
      }

      observer.disconnect();
      stop.current = () => {};
    };
  }, []);
}
